/**
 * Zustand store for global error handling and backend status management
 * Centralized error state management for React-conform error handling
 */

import React from 'react';
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { log } from '../utils/errors/logger';
import {
  AppError,
  BackendStatus,
  CriticalErrorType,
  extractErrorMessage,
  extractStatusCode,
  hasHttpResponse,
} from '../types/errors';
import {
  HEALTH_CHECK_CONFIG,
  ERROR_DISPLAY_CONFIG,
  HTTP_STATUS,
} from '../constants';

interface ErrorState {
  // Current errors
  errors: AppError[];
  
  // Backend health status
  backendStatus: BackendStatus;
  lastHealthCheck: number | null;
  
  // Critical error state - overrides entire UI
  criticalError: CriticalErrorType;
  criticalErrorSince: number | null;
  criticalErrorCount: number;
  
  // UI state
  showGlobalErrorDialog: boolean;
  showBackendOfflineBanner: boolean;
  
  // Configuration
  maxErrors: number;
  healthCheckInterval: number;
  criticalErrorThreshold: number;
  retryAttempts: Record<string, number>;
}

interface ErrorActions {
  // Error management
  addError: (error: Omit<AppError, 'id' | 'timestamp'>) => string;
  removeError: (errorId: string) => void;
  clearErrors: (type?: AppError['type']) => void;
  
  // Backend status
  setBackendStatus: (status: BackendStatus) => void;
  checkBackendHealth: () => Promise<boolean>;
  startHealthChecking: () => void;
  stopHealthChecking: () => void;
  
  // Critical error management
  setCriticalError: (type: CriticalErrorType) => void;
  clearCriticalError: () => void;
  checkCriticalErrorThreshold: () => void;
  
  // UI state management
  showErrorDialog: (show: boolean) => void;
  showOfflineBanner: (show: boolean) => void;
  
  // Error handling utilities
  handleApiError: (error: unknown, context?: string) => Promise<string>;
  handleNetworkError: (error: unknown, context?: string) => string;
  handleAuthError: (error: unknown, context?: string) => Promise<string>;
  
  // Retry logic
  canRetry: (errorId: string) => boolean;
  incrementRetry: (errorId: string) => void;
  resetRetries: (errorId: string) => void;
  
  // Reset
  reset: () => void;
}

type ErrorStore = ErrorState & ErrorActions;

let healthCheckInterval: NodeJS.Timeout | null = null;
// Throttle health checks to prevent excessive calls
let lastHealthCheckTime = 0;
let healthCheckInProgress = false;

export const useErrorStore = create<ErrorStore>()(
  devtools(
    persist(
      (set, get) => ({
      // Initial State
      errors: [],
      backendStatus: 'unknown',
      lastHealthCheck: null,
      
      // Critical error state
      criticalError: null,
      criticalErrorSince: null,
      criticalErrorCount: 0,
      
      // UI state
      showGlobalErrorDialog: false,
      showBackendOfflineBanner: false,
      
      // Configuration
      maxErrors: ERROR_DISPLAY_CONFIG.MAX_ERRORS,
      healthCheckInterval: HEALTH_CHECK_CONFIG.DEFAULT_INTERVAL_MS,
      criticalErrorThreshold: HEALTH_CHECK_CONFIG.CRITICAL_THRESHOLD,
      retryAttempts: {},

      // Error Management Actions
      addError: (errorData) => {
        const error: AppError = {
          ...errorData,
          id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: Date.now(),
        };

        set((state) => {
          const newErrors = [error, ...state.errors].slice(0, state.maxErrors);
          
          // Auto-show UI based on error type
          let showDialog = state.showGlobalErrorDialog;
          let showBanner = state.showBackendOfflineBanner;
          
          if (error.type === 'network' || error.code === 'BACKEND_UNAVAILABLE') {
            showBanner = true;
            set({ backendStatus: 'offline' });
          }
          
          if (error.type === 'auth' && error.status === 401) {
            showDialog = true;
          }
          
          return {
            errors: newErrors,
            showGlobalErrorDialog: showDialog,
            showBackendOfflineBanner: showBanner,
          };
        });

        log.error('Error added to store', { 
          errorId: error.id, 
          type: error.type, 
          message: error.message,
          context: error.context
        });

        return error.id;
      },

      removeError: (errorId) => {
        set((state) => ({
          errors: state.errors.filter(error => error.id !== errorId),
          retryAttempts: {
            ...state.retryAttempts,
            [errorId]: undefined
          }
        }));

        log.debug('Error removed from store', { errorId });
      },

      clearErrors: (type) => {
        set((state) => ({
          errors: type 
            ? state.errors.filter(error => error.type !== type)
            : [],
          retryAttempts: type 
            ? Object.keys(state.retryAttempts).reduce((acc, key) => {
                const error = state.errors.find(e => e.id === key);
                if (error?.type !== type) {
                  acc[key] = state.retryAttempts[key];
                }
                return acc;
              }, {} as Record<string, number>)
            : {}
        }));

        log.debug('Errors cleared from store', { type: type || 'all' });
      },

      // Backend Status Management
      setBackendStatus: (status) => {
        const { backendStatus: currentStatus } = get();
        
        if (currentStatus !== status) {
          set({ 
            backendStatus: status,
            lastHealthCheck: Date.now()
          });

          // Update UI state based on status
          if (status === 'offline') {
            set({ showBackendOfflineBanner: true });
          } else if (status === 'online') {
            set({ showBackendOfflineBanner: false });
            // Clear network errors when back online
            get().clearErrors('network');
          }

          log.info('Backend status changed', { 
            from: currentStatus, 
            to: status 
          });
        }
      },

      checkBackendHealth: async () => {
        // Throttle health checks to prevent excessive calls
        const now = Date.now();
        if (healthCheckInProgress || (now - lastHealthCheckTime < HEALTH_CHECK_CONFIG.THROTTLE_MS)) {
          // Skip logging for health checks to reduce noise
          // Return current status instead of triggering a new check
          return get().backendStatus === 'online';
        }
        
        healthCheckInProgress = true;
        lastHealthCheckTime = now;
        
        const { setBackendStatus, checkCriticalErrorThreshold } = get();
        
        try {
          // Use dedicated health check endpoint that doesn't require authentication
          const response = await fetch('/api/health', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            },
            // Don't include auth headers for health check
            signal: AbortSignal.timeout(HEALTH_CHECK_CONFIG.TIMEOUT_MS)
          });
          
          if (response.ok) {
            const healthData = await response.json();
            if (healthData.status === 'healthy') {
              setBackendStatus('online');
              
              // Reset critical error count on successful health check
              set((state) => ({
                criticalErrorCount: 0,
                criticalError: null,
                criticalErrorSince: null
              }));
              
              // Silently handle successful health checks
              healthCheckInProgress = false;
              return true;
            }
          }
          
          setBackendStatus('offline');
          
          // Only increment error count once per throttle period
          set((state) => ({
            criticalErrorCount: state.criticalErrorCount + 1
          }));
          
          // Check if we've hit critical threshold
          checkCriticalErrorThreshold();
          
          // Silently handle failed health checks
          healthCheckInProgress = false;
          return false;
        } catch (error) {
          // Silently handle health check errors
          
          setBackendStatus('offline');
          
          // Only increment error count once per throttle period
          set((state) => ({
            criticalErrorCount: state.criticalErrorCount + 1
          }));
          
          // Check if we've hit critical threshold
          checkCriticalErrorThreshold();
          
          healthCheckInProgress = false;
          return false;
        }
      },

      startHealthChecking: () => {
        const { healthCheckInterval: interval, checkBackendHealth } = get();
        
        // Clear existing interval
        if (healthCheckInterval) {
          clearInterval(healthCheckInterval);
        }
        
        // Start new interval
        healthCheckInterval = setInterval(async () => {
          await checkBackendHealth();
        }, interval);

        // Do initial check
        checkBackendHealth();
        
        log.info('Health checking started', { interval });
      },

      stopHealthChecking: () => {
        if (healthCheckInterval) {
          clearInterval(healthCheckInterval);
          healthCheckInterval = null;
        }
        
        log.info('Health checking stopped');
      },

      // UI State Management
      showErrorDialog: (show) => {
        set({ showGlobalErrorDialog: show });
      },

      showOfflineBanner: (show) => {
        set({ showBackendOfflineBanner: show });
      },

      // Error Handling Utilities
      handleApiError: async (error: unknown, context?: string): Promise<string> => {
        const { addError, checkBackendHealth } = get();

        let errorType: AppError['type'] = 'server';
        let message = extractErrorMessage(error);
        let status = extractStatusCode(error);
        let code: string | undefined;
        let retryable = false;

        // Parse error response
        if (hasHttpResponse(error)) {
          status = error.response.status;
          message = error.response.data?.message || error.response.statusText || message;
          code = error.response.data?.code;

          // Smart 401 error handling: Check if backend is actually down
          if (status === HTTP_STATUS.UNAUTHORIZED) {
            // Check if backend is down without logging (reduces noise)
            
            // Quick health check to distinguish between auth errors and backend down
            const isBackendHealthy = await checkBackendHealth();
            
            if (!isBackendHealthy) {
              // Backend is down - this is NOT an auth error
              return get().handleNetworkError(error, context);
            } else {
              // Backend is up - this is a real auth error
              errorType = 'auth';
              message = 'Your session has expired. Please log in again.';
            }
          } else if (status === 403) {
            errorType = 'auth';
            message = 'You do not have permission to perform this action.';
          } else if (status === 422) {
            errorType = 'validation';
            retryable = false;
          } else if (status && status >= 500) {
            errorType = 'server';
            retryable = true;
          }
        } else if (error?.message) {
          message = error.message;
          code = error.code;
          
          // Check for network errors
          if (error.message.toLowerCase().includes('fetch') || 
              error.message.toLowerCase().includes('network')) {
            return get().handleNetworkError(error, context);
          }
        }

        return addError({
          type: errorType,
          message,
          status,
          code,
          context,
          retryable,
          details: error
        });
      },

      handleNetworkError: (error, context) => {
        const { addError, setBackendStatus } = get();
        
        // Set backend as offline
        setBackendStatus('offline');
        
        // Create context-aware error message
        let message = 'Server is currently unavailable. Please try again later.';
        
        // Enhanced messages based on context
        if (context?.toLowerCase().includes('login') || context?.toLowerCase().includes('auth')) {
          message = 'Cannot connect to server for authentication. Please check your connection and try again.';
        } else if (context?.toLowerCase().includes('chat')) {
          message = 'Chat service is currently unavailable. Please try again in a few moments.';
        } else if (context?.toLowerCase().includes('document')) {
          message = 'Document service is currently unavailable. Your files are safe, please try again later.';
        } else if (context?.toLowerCase().includes('search')) {
          message = 'Search service is temporarily unavailable. Please try again shortly.';
        }
        
        log.info('Backend detected as offline', { 
          context, 
          errorType: error?.constructor?.name,
          message: error?.message
        });
        
        return addError({
          type: 'network',
          message,
          code: 'BACKEND_UNAVAILABLE',
          context,
          retryable: true,
          details: error
        });
      },

      handleAuthError: async (error, context) => {
        const { addError, checkBackendHealth } = get();
        
        // For explicit auth errors, also check if backend is down first
        log.debug('Handling explicit auth error, checking backend health...', { context });
        
        const isBackendHealthy = await checkBackendHealth();
        
        if (!isBackendHealthy) {
          // Backend is down - this is NOT an auth error
          return get().handleNetworkError(error, context);
        }
        
        const errorId = addError({
          type: 'auth',
          message: 'Your session has expired. Please log in again.',
          status: 401,
          code: 'UNAUTHORIZED',
          context,
          retryable: false,
          details: error
        });

        // Trigger auth error handling (will be handled by Error Boundary)
        return errorId;
      },

      // Retry Logic
      canRetry: (errorId) => {
        const { errors, retryAttempts } = get();
        const error = errors.find(e => e.id === errorId);
        
        if (!error?.retryable) {
          return false;
        }
        
        const attempts = retryAttempts[errorId] || 0;
        return attempts < 3; // Max 3 retries
      },

      incrementRetry: (errorId) => {
        set((state) => ({
          retryAttempts: {
            ...state.retryAttempts,
            [errorId]: (state.retryAttempts[errorId] || 0) + 1
          }
        }));
      },

      resetRetries: (errorId) => {
        set((state) => ({
          retryAttempts: {
            ...state.retryAttempts,
            [errorId]: 0
          }
        }));
      },

      // Critical Error Management
      setCriticalError: (type) => {
        const currentTime = Date.now();
        
        set({
          criticalError: type,
          criticalErrorSince: currentTime
        });
        
        log.error('Critical error state activated', { 
          type, 
          timestamp: currentTime,
          criticalErrorCount: get().criticalErrorCount
        });
      },

      clearCriticalError: () => {
        const { criticalError } = get();
        
        if (criticalError) {
          log.info('Critical error state cleared', { 
            previousType: criticalError,
            duration: Date.now() - (get().criticalErrorSince || 0)
          });
        }
        
        set({
          criticalError: null,
          criticalErrorSince: null,
          criticalErrorCount: 0
        });
      },

      checkCriticalErrorThreshold: () => {
        const { criticalErrorCount, criticalErrorThreshold, setCriticalError } = get();
        
        if (criticalErrorCount >= criticalErrorThreshold) {
          log.warn('Critical error threshold reached', { 
            count: criticalErrorCount, 
            threshold: criticalErrorThreshold 
          });
          
          setCriticalError('backend_down');
        }
      },

      // Reset
      reset: () => {
        const { stopHealthChecking } = get();
        stopHealthChecking();
        
        set({
          errors: [],
          backendStatus: 'unknown',
          lastHealthCheck: null,
          criticalError: null,
          criticalErrorSince: null,
          criticalErrorCount: 0,
          showGlobalErrorDialog: false,
          showBackendOfflineBanner: false,
          retryAttempts: {}
        });
        
        log.info('Error store reset');
      }
    }),
      {
        name: 'error-store',
        partialize: (state) => ({
          // Don't persist errors or UI state, only configuration and critical error info
          maxErrors: state.maxErrors,
          healthCheckInterval: state.healthCheckInterval,
          criticalErrorThreshold: state.criticalErrorThreshold,
          backendStatus: state.backendStatus === 'online' ? 'unknown' : state.backendStatus,
          // Persist critical error state for recovery after refresh
          criticalError: state.criticalError,
          criticalErrorSince: state.criticalErrorSince
        }),
      }
    ),
    { name: 'ErrorStore' }
  )
);

// Hook for easier access to error handling
// Global health check management - only start once
let healthCheckStarted = false;

export const useErrorHandler = () => {
  const store = useErrorStore();

  // Auto-start health checking only once globally
  React.useEffect(() => {
    if (!healthCheckStarted) {
      healthCheckStarted = true;
      store.startHealthChecking();
    }
    
    // Don't stop health checking when this hook unmounts
    // Keep it running for the entire app lifecycle
  }, [store]);

  return {
    // Current state
    errors: store.errors,
    backendStatus: store.backendStatus,
    isBackendOffline: store.backendStatus === 'offline',
    hasErrors: store.errors.length > 0,
    
    // Critical error state
    criticalError: store.criticalError,
    hasCriticalError: store.criticalError !== null || (store.backendStatus === 'offline' && store.criticalErrorCount >= 1),
    criticalErrorSince: store.criticalErrorSince,
    criticalErrorCount: store.criticalErrorCount,
    
    // UI state (boolean properties)
    showErrorDialogState: store.showGlobalErrorDialog,
    showOfflineBanner: store.showBackendOfflineBanner,
    
    // Actions
    addError: store.addError,
    removeError: store.removeError,
    clearErrors: store.clearErrors,
    
    // Error handlers
    handleApiError: store.handleApiError,
    handleNetworkError: store.handleNetworkError,
    handleAuthError: store.handleAuthError,
    
    // Retry logic
    canRetry: store.canRetry,
    retry: store.incrementRetry,
    resetRetries: store.resetRetries,
    
    // Critical error actions
    setCriticalError: store.setCriticalError,
    clearCriticalError: store.clearCriticalError,
    
    // UI actions (functions)
    showErrorDialog: store.showErrorDialog,  // The actual function
    dismissErrorDialog: () => store.showErrorDialog(false),
    dismissOfflineBanner: () => store.showOfflineBanner(false),
    
    // Health check
    checkHealth: store.checkBackendHealth,
    lastHealthCheck: store.lastHealthCheck
  };
};

// Hook specifically for components that need to handle API errors
export const useApiErrorHandler = () => {
  const { handleApiError, handleNetworkError, handleAuthError } = useErrorHandler();
  
  return React.useCallback(async (error: unknown, context?: string) => {
    // Determine error type and handle accordingly
    if (error?.response?.status === 401) {
      return await handleAuthError(error, context);
    }
    
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return handleNetworkError(error, context);
    }
    
    return await handleApiError(error, context);
  }, [handleApiError, handleNetworkError, handleAuthError]);
};